/**
 * Quote-coefficient defaults and the "AI result" snapshot as pure functions, so the browser and the server's estimate
 * jobs (JF 2026-10-16: a closed browser no longer stops an estimate) give a new AI sheet exactly the same start.
 * Levels: the AM's set for the quantity → the company's for it → the AM's base → the company base → the template.
 * "For it" = the largest saved quantity not above it; if neither has one, the smallest saved.
 */
import { DEFAULT_QCOEF } from "./costing.ts";
import type { QCoef, Sheet } from "./types.ts";

export interface CoefPrefs { base?: QCoef | null; byQty?: Record<string, QCoef> }
export interface CoefPrefsPair { mine: CoefPrefs | null; company: CoefPrefs | null }

const full = (c: Partial<QCoef>): QCoef => ({ ...DEFAULT_QCOEF, ...c });

/** The base defaults: mine, else the company base, else the template. */
export const baseCoef = (p: CoefPrefsPair | null | undefined): QCoef => full(p?.mine?.base || p?.company?.base || {});

/** The saved set that applies to `qty`: the largest saved quantity not above it ("fits"), else the smallest (fallback). */
export function coefAt(byQty: Record<string, QCoef> | undefined, qty: number, fallback = true): QCoef | null {
  const qs = Object.keys(byQty || {}).map(Number).filter((q) => q > 0).sort((a, b) => a - b);
  const q = [...qs].reverse().find((x) => x <= qty) ?? (fallback ? qs[0] : undefined);
  return q == null ? null : byQty![String(q)];
}

/** The defaults for one order quantity. A set saved at or below it (mine first) beats the smallest-saved fallback. */
export function coefFor(p: CoefPrefsPair | null | undefined, qty: number | undefined): QCoef {
  if (!qty) return baseCoef(p);
  const m = p?.mine?.byQty, co = p?.company?.byQty;
  const c = coefAt(m, qty, false) || coefAt(co, qty, false) || coefAt(m, qty) || coefAt(co, qty);
  return c ? full(c) : baseCoef(p);
}

/** Are there defaults for particular quantities (mine or the company's)? */
export const hasQtyCoef = (p: CoefPrefsPair | null | undefined): boolean =>
  !!(Object.keys(p?.mine?.byQty || {}).length || Object.keys(p?.company?.byQty || {}).length);

/** A new AI sheet starts with the defaults for its quantity (or each quantity of a tiered quote). Mutates and returns it. */
export function applyCoefPrefs(sheet: Sheet, p: CoefPrefsPair | null | undefined): Sheet {
  if (!hasQtyCoef(p)) return sheet;
  if (sheet.tiers?.qtys.length) {
    sheet.tiers.coef = Object.fromEntries(sheet.tiers.qtys.map((q) => [String(q), coefFor(p, q)]));
    if (sheet.tiers.active != null) sheet.qcoef = { ...sheet.tiers.coef[String(sheet.tiers.active)] };
  } else if (Number(sheet.h.qty) > 0) sheet.qcoef = { ...coefFor(p, Number(sheet.h.qty)) };
  return sheet;
}

/** "Reset to the AI result": the parts of a sheet that count as the AI result — no sketch, no snapshot inside the snapshot, no open / closed state. */
export function aiSnapshot(s: Sheet): string {
  const ai = s.ai ? { ...s.ai, base: undefined, open: undefined } : null;
  return JSON.stringify({ ...s, sketch: "", ai });
}

/** Mark this sheet as a fresh AI result. */
export function withAiBase(s: Sheet): Sheet {
  if (!s.ai) return s;
  s.ai.base = aiSnapshot(s);
  return s;
}
